import { useReducer } from 'react'
import { getAuthState, __isDebugOn } from '@sparky/framework'

import type { Reducer, RefObject } from 'react'
import type {
  Tsg,
  ReadonlyTsgTree,
  ReadonlyTsgInstances,
  AppManifests,
  SupportAccount,
  AuthState,
} from '@sparky/framework'
import type { Action } from './actions'
import type { ActivationManifest, ScreenComponentDef } from './renderer'

export const authStateRef = Object.freeze(Object.defineProperties({}, {
  current: { get: getAuthState }
})) as RefObject<AuthState>

export type ActivationInstance = {
  app_id: string,
  parent_app_id?: string,
  is_disabled?: boolean,
  license_id?: string,
  activate_on_root?: boolean,
  activation_type: 'NEW' | 'AMEND',
  allocation_type?: 'FULL' | 'SHARED',
  allocation?: any[],
  [key: string]: any,
}

export type ActivationData = {
  managementMethod?: 'cloud' | 'onprem'
  tsgCreateList?: Array<{id?: string, display_name: string, parent_id?: string}>
  readInstances?: boolean
  instances?: Record<string, ActivationInstance | undefined>
  [key: string]: any
}

export type AbsoluteRootTsg = {
  tsgId: string | undefined
  instances: ReadonlyTsgInstances
  error?: string
}

export type TSGDisplayType = 'default' | 'absolute_root_without_children' | 'absolute_root_with_children'

export type ActivationStep = {
  key: string
  index: number
  dependencies: number[]
  isAccordionEnabled: boolean
  isDone: boolean
}

export type CommonStoreState = {
  loading: boolean
  loadingTsg: boolean
  loadingHashes: boolean
  hash?: string
  context?: string
  screen?: ScreenComponentDef
  manifest?: ActivationManifest
  globalTsgId: string
  tsg?: Tsg
  tsgs: ReadonlyTsgTree
  absoluteRootTsg?: AbsoluteRootTsg | undefined
  instances: ReadonlyTsgInstances
  apps: AppManifests
  platformRegions: PlatformRegions
  supportAccounts: IndexedSupportAccounts
  supportAccountId: string
  licenses: Licenses
  lockedInfo: LockedInfo
  hashDecodeError: ErrorObject | undefined
  isHashFullyActivated: boolean
  tsgDisplayType?: TSGDisplayType
  activationData?: ActivationData
  selectedPlatformRegion?: string
  lockFullAllocation?: boolean // currently use this for use exsting CDL (migrating CDL, or standalone CDL inside TSG)
  // activationSteps required for unified, not needed for OneApp
  activationSteps: {
    [key: string]: ActivationStep
  }
}

export type PlatformRegion = { name: string, display: string }
export type PlatformRegions = PlatformRegion[] & { get: (name: string) => PlatformRegion | undefined }
export type IndexedSupportAccounts = SupportAccount[] & { get: (name: string | number) => SupportAccount | undefined }

export type Dispatch = (action: Action) => void
export type StateRef = {
  current: CommonStoreState
  dispatch: Dispatch
}
export type Store = {
  dispatch: Dispatch
  getState: () => CommonStoreState
  stateRef: StateRef
}
export type SubLicense = {
  auth_code: string
  license_type: string
  license_display_name: string
  purchased_size: number
  purchased_unit: string
  is_ela: boolean
  license_version?: string
  is_csp_activated: boolean
  [otherProperties: string]: unknown
}
export type License = {
  app_id: string
  hash: string
  licenses: SubLicense[]
  extra?: any
  license_id?: string
  csp_tenant_id?: string
  [otherProperties: string]: unknown
}
export type Licenses = License[] & { get: (app_id: string) => License | undefined }
export type LockedInfo = {
  tsg_ids?: string[]
  support_account_ids?: string[]
  regions?: string[]
  app_id?: string
  csp_id?: string
  mode?: string
  platform?: 'cloud' | 'onprem'
} | undefined

export type ContextApp = {
  [app_id: string]: string
}

export const stateRef: StateRef = Object.seal({ // same as createRef
  current: {} as CommonStoreState, // ensure not null
  dispatch: (() => {
    throw new Error('state store is not initialized yet')
  }) as Dispatch
})

export type ErrorObject = {
  msg?: string
  message?: string
  error?: string
  statusCode?: number
}

export const getState = (): CommonStoreState => stateRef.current

export const dispatch: Dispatch = (action) => stateRef.dispatch(action)

export const store = {
  dispatch,
  getState,
  stateRef,
} as Store

export const useStoreReducer = (reducer: Reducer<CommonStoreState, Action>, initState: {}, initializer: (initState: {}) => CommonStoreState): CommonStoreState => {
  const [state, dispatchFn] = useReducer(
    (state: CommonStoreState, action: Action) => {
      const newState = reducer(state, action)
      stateRef.current = newState
      return newState
    },
    initState,
    initializer,
  )
  if (stateRef.dispatch !== dispatchFn) {
    stateRef.dispatch = dispatchFn
  }
  return state
}

// export const StateContext = createContext(store)

if (__isDebugOn()) {
  let commonModule = window.__SPARKY_LIBS__.commonModule
  if (!commonModule) {
    commonModule = {}
    Object.defineProperties(window.__SPARKY_LIBS__.__proto__, {
      commonModule: {
        value: commonModule
      }
    })
  }
  commonModule.activationStore = store
  commonModule.activationStateRef = stateRef
}

export default store
